import { useState, useEffect, useCallback, useMemo } from 'react';
import './App.css';

interface AgeResult {
  years: number;
  months: number;
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  totalMonths: number;
  totalWeeks: number;
  totalDays: number;
  totalHours: number;
  totalMinutes: number;
  totalSeconds: number;
  nextBirthday: {
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
  };
  isBirthday: boolean;
  dayOfWeek: string;
}

function App() {
  const [birthDate, setBirthDate] = useState('');
  const [ageResult, setAgeResult] = useState<AgeResult | null>(null);
  
  // Get user's locale and date format preferences
  const { dateFormat, timeFormat } = useMemo(() => {
    const userLocale = navigator.language || 'en-US';
    const dateTimeFormat = new Intl.DateTimeFormat(userLocale);
    const format = dateTimeFormat.formatToParts(new Date())
      .map(({ type, value }) => {
        switch (type) {
          case 'day': return 'dd';
          case 'month': return 'MM';
          case 'year': return 'yyyy';
          default: return value;
        }
      })
      .join('')
      .replace(/[^\w\s]/g, '-') // Replace non-word characters with hyphens
      .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
      .replace(/^-|-$/g, ''); // Remove leading/trailing hyphens

    return {
      dateFormat: format,
      timeFormat: 'HH:mm', // 24-hour format by default
      is24Hour: !new Intl.DateTimeFormat(userLocale, { hour: 'numeric' })
        .formatToParts(new Date())
        .some(part => part.type === 'dayPeriod')
    };
  }, []);

  const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'] as const;

  const calculateAge = useCallback((birthDate: string) => {
    if (!birthDate) return;
    console.log('calculateAge called with:', birthDate);
    // If time is not provided, set it to midnight (00:00:00)
    const dateString = birthDate.includes('T') ? birthDate : `${birthDate}T00:00:00`;
    const birth = new Date(dateString);
    const now = new Date();
    
    // If the date is invalid or in the future, don't proceed with calculation
    if (isNaN(birth.getTime()) || birth > now) {
      console.error('Invalid or future date');
      return;
    }
    
    const diff = now.getTime() - birth.getTime();

    // --- Accurate Age Calculation (Years, Months, Days) ---
    let years = now.getFullYear() - birth.getFullYear();
    let months = now.getMonth() - birth.getMonth();
    let days = now.getDate() - birth.getDate();
    let hours = now.getHours() - birth.getHours();
    let minutes = now.getMinutes() - birth.getMinutes();
    let seconds = now.getSeconds() - birth.getSeconds();

    if (seconds < 0) {
      minutes--;
      seconds += 60;
    }
    if (minutes < 0) {
      hours--;
      minutes += 60;
    }
    if (hours < 0) {
      days--;
      hours += 24;
    }
    if (days < 0) {
      months--;
      // Borrow days from the previous month
      const lastMonth = new Date(now.getFullYear(), now.getMonth(), 0);
      days += lastMonth.getDate();
    }
    if (months < 0) {
      years--;
      months += 12;
    }
    
    console.log('Calculated time:', { years, months, days, hours, minutes, seconds });

    // Calculate next birthday with leap day handling (Feb 29 -> Feb 28 on non-leap years)
    const isLeapYear = (y: number) => (y % 4 === 0 && y % 100 !== 0) || (y % 400 === 0);
    const birthMonth = birth.getMonth();
    const birthDateNum = birth.getDate();

    const buildBirthday = (year: number) => {
      let day = birthDateNum;
      // If born on Feb 29 and target year is not leap, celebrate on Feb 28
      if (birthMonth === 1 && birthDateNum === 29 && !isLeapYear(year)) {
        day = 28;
      }
      return new Date(
        year,
        birthMonth,
        day,
        birth.getHours(),
        birth.getMinutes(),
        birth.getSeconds()
      );
    };

    let nextBirthday = buildBirthday(now.getFullYear());
    if (nextBirthday < now) {
      nextBirthday = buildBirthday(now.getFullYear() + 1);
    }

    const timeUntilNextBirthday = nextBirthday.getTime() - now.getTime();
    const daysUntilBirthday = Math.floor(timeUntilNextBirthday / (1000 * 60 * 60 * 24));
    const hoursUntilBirthday = Math.floor((timeUntilNextBirthday % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutesUntilBirthday = Math.floor((timeUntilNextBirthday % (1000 * 60 * 60)) / (1000 * 60));
    const secondsUntilBirthday = Math.floor((timeUntilNextBirthday % (1000 * 60)) / 1000);

    // Check if today is birthday (with Feb 29 -> Feb 28 rule on non-leap years)
    const todayIsLeapObserved = (birthMonth === 1 && birthDateNum === 29 && !isLeapYear(now.getFullYear()) && now.getMonth() === 1 && now.getDate() === 28);
    const isBirthday = todayIsLeapObserved || (
      now.getMonth() === birthMonth && now.getDate() === birthDateNum
    );

    // --- Total Age in Different Units ---
    const totalSeconds = Math.floor(diff / 1000);
    const totalMinutes = Math.floor(diff / (1000 * 60));
    const totalHours = Math.floor(diff / (1000 * 60 * 60));
    // DST-safe day difference using UTC midnights
    const birthUTCmid = Date.UTC(birth.getUTCFullYear(), birth.getUTCMonth(), birth.getUTCDate());
    const nowUTCmid = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
    const totalDays = Math.floor((nowUTCmid - birthUTCmid) / (1000 * 60 * 60 * 24));
    const totalWeeks = Math.floor(totalDays / 7);

    let totalMonths = (now.getFullYear() - birth.getFullYear()) * 12 - birth.getMonth() + now.getMonth();
    // If we haven't reached the birth day/time in the current month, subtract one month
    const beforeBirthDay = now.getDate() < birth.getDate();
    const sameDayBeforeTime = now.getDate() === birth.getDate() && (
      now.getHours() < birth.getHours() ||
      (now.getHours() === birth.getHours() && (now.getMinutes() < birth.getMinutes() ||
        (now.getMinutes() === birth.getMinutes() && now.getSeconds() < birth.getSeconds())))
    );
    if (beforeBirthDay || sameDayBeforeTime) {
      totalMonths--;
    }

    const result = {
      years,
      months,
      days,
      hours,
      minutes,
      seconds,
      totalMonths,
      totalWeeks,
      totalDays,
      totalHours,
      totalMinutes,
      totalSeconds,
      nextBirthday: {
        days: daysUntilBirthday,
        hours: hoursUntilBirthday,
        minutes: minutesUntilBirthday,
        seconds: secondsUntilBirthday,
      },
      isBirthday,
      dayOfWeek: daysOfWeek[birth.getDay()],
    };
    
    console.log('Setting age result:', result);
    setAgeResult(result);

  }, []);


  // Initial calculation when component mounts
  useEffect(() => {
    if (birthDate) {
      calculateAge(birthDate);
    }
  }, [birthDate, calculateAge]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
      <div className="max-w-2xl mx-auto bg-white rounded-xl shadow-md overflow-hidden p-6 mt-10">
        <h1 className="text-3xl font-bold text-center text-indigo-700 mb-6">Computan Age Calculator</h1>
        
        <div className="space-y-6">
          <div className="space-y-2">
            <label htmlFor="birthDate" className="block text-sm font-medium text-gray-700">
              Enter your birth date and time:
            </label>
            <div className="space-y-2">
              <div>
                <input
                  type="date"
                  id="birthDate"
                  value={birthDate.split('T')[0]}
                  onChange={(e) => setBirthDate(e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  placeholder={dateFormat}
                  title={`Enter date in ${dateFormat} format`}
                  required
                />
              </div>
              <div>
                <input
                  type="time"
                  id="birthTime"
                  value={birthDate.includes('T') ? birthDate.split('T')[1].substring(0, 5) : '00:00'}
                  onChange={(e) => {
                    const datePart = birthDate.split('T')[0] || new Date().toISOString().split('T')[0];
                    setBirthDate(`${datePart}T${e.target.value}:00`);
                  }}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  step={60} // Show only hours and minutes
                  title={`Enter time in ${timeFormat} format`}
                />
              </div>
            </div>
          </div>
        </div>

        {ageResult && (
          <div className="mt-8 space-y-4">
            <div className="flex justify-center">
              <img 
                src={`https://robohash.org/${ageResult.years}?set=set4`} 
                alt={`Robot avatar for age ${ageResult.years}`}
                className="h-32 w-32 rounded-full border-4 border-indigo-200"
              />
            </div>
            <div className="bg-indigo-50 p-4 rounded-lg">
              <h2 className="text-lg font-semibold text-indigo-800">Your Age</h2>
              <p className="text-gray-900">
                {ageResult.years} years, {ageResult.months} months, {ageResult.days} days
              </p>
              <p className="text-sm text-gray-700">
                {ageResult.hours} hours, {ageResult.minutes} minutes, {ageResult.seconds} seconds
              </p>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="text-md font-semibold text-gray-800">...or in other units:</h3>
              <div className="text-gray-700 grid grid-cols-1 sm:grid-cols-2 gap-x-6">
                  <div><span className="text-gray-500 mr-2">or</span> <strong>{ageResult.totalMonths.toLocaleString()}</strong> months old</div>
                  <div><span className="text-gray-500 mr-2">or</span> <strong>{ageResult.totalWeeks.toLocaleString()}</strong> weeks old</div>
                  <div><span className="text-gray-500 mr-2">or</span> <strong>{ageResult.totalDays.toLocaleString()}</strong> days old</div>
                  <div><span className="text-gray-500 mr-2">or</span> <strong>{ageResult.totalHours.toLocaleString()}</strong> hours old</div>
                  <div><span className="text-gray-500 mr-2">or</span> <strong>{ageResult.totalMinutes.toLocaleString()}</strong> minutes old</div>
                  <div><span className="text-gray-500 mr-2">or</span> <strong>{ageResult.totalSeconds.toLocaleString()}</strong> seconds old</div>
              </div>
            </div>

            <div className="bg-green-50 p-4 rounded-lg">
              <h2 className="text-lg font-semibold text-green-800">Born on a <span className="text-green-900">{ageResult.dayOfWeek}</span></h2>
            </div>

            {ageResult.isBirthday ? (
              <div className="bg-yellow-50 p-4 rounded-lg text-center">
                <h2 className="text-xl font-bold text-yellow-900 mb-2">🎉 Happy Birthday! 🎂</h2>
                <p className="text-yellow-900">You are {ageResult.years} years old today!</p>
              </div>
            ) : (
              <div className="bg-blue-50 p-4 rounded-lg">
                <h2 className="text-lg font-semibold text-blue-800">Next Birthday</h2>
                <p className="text-blue-700">
                  <span className="text-blue-900">{ageResult.nextBirthday.days} days</span>, <span className="text-blue-900">{ageResult.nextBirthday.hours} hours</span>,{' '}
                  <span className="text-blue-900">{ageResult.nextBirthday.minutes} minutes</span>, <span className="text-blue-900">{ageResult.nextBirthday.seconds} seconds</span>
                </p>
              </div>
            )}
          </div>
        )}

        {ageResult?.isBirthday && (
          <div className="mt-6 p-4 bg-pink-50 rounded-lg text-center">
            <div className="text-4xl mb-2">🎂</div>
            <div className="flex flex-wrap justify-center gap-1 mb-4 max-w-full overflow-x-auto">
              {Array.from({ length: ageResult.years }).map((_, i) => (
                <span key={i} className="text-yellow-500 text-xl">🕯️</span>
              ))}
            </div>
            <p className="text-pink-800 font-medium">Blow out the candles and make a wish!</p>
          </div>
        )}
      </div>
      
      <footer className="mt-10 text-center text-sm text-gray-500">
        <p>Computan Age Calculator © {new Date().getFullYear()}</p>
      </footer>
    </div>
  );
}

export default App;
